import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { carryFilters } from "@/lib/filters";

import type { Page } from "../../api";
import { OptionLink } from "../../links";
import { TabPage } from "../../PageFrame";
import type { PageProps } from "../index";
import { f2, isInactive, MUTED_ROW, OptionFlags, PagedTable, PageHead, RatingBar, RefLegend, sortParams, usePage, usePer, useTableSort, type TableSort } from "../kit";
import { CriteriaListSection, HeatmapSection, pagePhase, SectionCard } from "./CriteriaByPhase";

export interface PhaseScore {
  phase: string;
  score: number | null;
  scored_n: number;
  expected_n: number;
}

export interface RankingRow {
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  funnel: string;
  phase: string | null;
  dropped: boolean;
  rating: number | null;
  criteria_count: number;
  not_scored: boolean;
  low_evidence: boolean;
  rank_n: number;
  criteria_total: number;
  phase_scores: PhaseScore[];
}

/** A row click opens Criteria Ranking for that option: filters kept, page number left behind. */
export function evidenceSearch(previous: Record<string, unknown>, optionId: string): Record<string, unknown> {
  return { ...carryFilters(previous), f_option: optionId };
}

export const phaseSortId = (phase: string) => `phase_${phase.split(":")[0].trim()}`;

export function phasesWithCriteria(items: RankingRow[]): string[] {
  return (items[0]?.phase_scores ?? [])
    .map((score) => score.phase)
    .filter((phase) => items.some((row) =>
      (row.phase_scores.find((score) => score.phase === phase)?.expected_n ?? 0) > 0));
}

export function rankingColumns(phases: string[]): ColumnDef<RankingRow, unknown>[] {
  const phaseColumns: ColumnDef<RankingRow, unknown>[] = phases.map((phase) => ({
    id: phaseSortId(phase),
    header: phase,
    meta: { align: "right", sortable: true },
    cell: ({ row }) => {
      const score = row.original.phase_scores.find((entry) => entry.phase === phase);
      if (!score || score.expected_n === 0) return <span className="text-ks-grey-1">No criteria</span>;
      const title = `${score.scored_n} of ${score.expected_n} rated`;
      if (score.scored_n === 0) return <span title={title}>–</span>;
      return <span title={title}>{f2(score.score)}</span>;
    },
  }));

  return [
    { id: "rank", header: "#", meta: { sortable: true }, cell: ({ row }) => <span className="text-ks-grey-1">{row.original.not_scored ? "–" : row.original.rank_n}</span> },
    {
      id: "option",
      header: "Option",
      meta: { sortable: true },
      cell: ({ row }) => (
        // The row itself is clickable; a click on the name must open the option, not the row's target.
        <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <OptionLink optionId={row.original.option_id} itemId={row.original.item_id} from="ranking_table">
            {row.original.option_name}
          </OptionLink>
        </span>
      ),
    },
    { id: "rating", header: "Overall", meta: { align: "right", sortable: true }, cell: ({ row }) => f2(row.original.rating) },
    ...phaseColumns,
    { id: "bar", header: () => <RefLegend />, cell: ({ row }) => <RatingBar value={row.original.rating} className="w-56 min-w-0" /> },
    { id: "criteria", header: "Criteria", meta: { align: "right", sortable: true }, cell: ({ row }) => row.original.criteria_count },
    { id: "flags", header: "Flags", cell: ({ row }) => <OptionFlags row={row.original} /> },
  ];
}

export const RANKING_COLUMNS = rankingColumns([]);

export function RankingTableBody({
  data,
  page,
  onPage,
  onOpen,
  phase = null,
  per,
  onPer,
  sort = null,
  onSort,
}: {
  data: Page<RankingRow>;
  page: number;
  onPage: (next: number) => void;
  onOpen: (row: RankingRow) => void;
  phase?: string | null;
  per?: number;
  onPer?: (next: 10 | 25) => void;
  sort?: TableSort; onSort?: (id: string) => void;
}) {
  return (
    <PagedTable<RankingRow>
      columns={rankingColumns(phase ? [phase] : phasesWithCriteria(data.items))}
      data={data}
      page={page}
      onPage={onPage}
      per={per}
      onPer={onPer}
      caption="Options ranked by rating"
      rowClassName={(row) => (isInactive(row) ? MUTED_ROW : undefined)}
      onRowClick={onOpen}
      sort={sort}
      onSort={onSort}
    />
  );
}

export function RankingSection({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const phase = pagePhase(search);
  const [page, setPage] = usePage();
  const [per, setPer] = usePer();
  const [sort, onSort] = useTableSort();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  const open = (row: RankingRow) =>
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area ?? "sourcing", module: params.module ?? "sourcing_funnel", tab: "criteria_ranking" },
      search: ((previous: Record<string, unknown>) => evidenceSearch(previous, row.option_id)) as never,
    } as never);
  return (
    <TabPage<Page<RankingRow>>
      tab={tab}
      areaName={areaName}
      extra={{ ...sortParams(sort), ...(phase ? { criteria_phase: phase } : {}), limit: per, offset: (page - 1) * per }}
      isEmpty={(data) => data.total === 0}
      head={(data) => {
        const first = data?.items[0];
        return (
          <PageHead
            title={tab.label}
            sub={first ? `${first.item_name} · click an option to see its evidence` : undefined}
            kpi={first ? { label: "Total criteria", value: first.criteria_total } : null}
          />
        );
      }}
    >
      {(data) => <RankingTableBody data={data} page={page} onPage={setPage} onOpen={open} phase={phase} per={per} onPer={setPer} sort={sort} onSort={onSort} />}
    </TabPage>
  );
}

export function RankingTablePage({ tab, areaName }: PageProps) {
  return (
    <>
      <SectionCard>
        <RankingSection tab={tab} areaName={areaName} />
      </SectionCard>
      <HeatmapSection tab={tab} areaName={areaName} />
      <CriteriaListSection tab={tab} areaName={areaName} />
    </>
  );
}
